/**
 * Paint color visualizer — the Home Depot pitch demo.
 *
 * This scene proves the NSPP engine's value for retail product
 * configurators. The architecture is identical to the chase demo:
 *   - LEFT panel: DOM inputs (brand picker, color swatches, sheen
 *     slider, coverage slider, custom hex input, room thumbnails).
 *   - RIGHT panel: a single canvas-rendered room photo whose visual
 *     state is mutated by the per-instance filter chain
 *     (hueRotate + saturate + brightness + contrast).
 *
 * Every input change mutates SceneNode fields directly. The engine
 * re-renders at 60 FPS, the shader applies the new filter values
 * on the next frame. No debounce, no submit, no waiting.
 *
 * Why this matters as a pitch:
 *   The shader (src/gl/rectPipeline.ts:218-326) does real
 *   RGB→HSL→RGB conversion and a hueRotate in radians. That's a
 *   paint-tint primitive. The instance buffer carries filter
 *   values per-node, so 6 different rooms + 12 paint colors =
 *   72 combinations rendered in 1 draw call.
 *   A traditional Three.js / bespoke WebGL approach rebuilds
 *   the pipeline per brand. NSPP reuses one scene.
 *
 * The point of the demo, said differently: the left-side inputs
 * are paint brand selectors. The right-side image is a wall.
 * Drag the sheen slider and watch the wall finish change. Tap
 * a swatch and watch the wall re-tint. Type a hex code and
 * watch the wall adopt your custom color. Same code path as the
 * chase demo; different domain.
 */

import {
  type Scene,
  type SceneNode,
  createScene,
  makeRect,
  makeText,
  makeImage,
  addNode,
} from "../sceneGraph";

// ─────────────────────────────────────────────────────────────────────────
// Room photos. Each is a real Unsplash interior with a prominent wall.
// On desktop the room takes the right 50%; on mobile it stacks below
// the form panel.
// ─────────────────────────────────────────────────────────────────────────
const ROOMS = [
  { id: "bedroom-dark",   url: "/rooms/bedroom-dark.jpg",   label: "Bedroom · Dark" },
  { id: "living-warm",    url: "/rooms/living-warm.jpg",    label: "Living · Warm" },
  { id: "kitchen-bright", url: "/rooms/kitchen-bright.jpg", label: "Kitchen · Bright" },
  { id: "kitchen-white",  url: "/rooms/kitchen-white.jpg",  label: "Kitchen · White" },
  { id: "living-colorful", url: "/rooms/living-colorful.jpg", label: "Living · Colorful" },
  { id: "dining-neutral", url: "/rooms/dining-neutral.jpg", label: "Dining · Neutral" },
];

// ─────────────────────────────────────────────────────────────────────────
// Paint brand palettes. Each brand has 8 signature colors. The demo
// proves that the engine handles multi-brand visualizers with the same
// code path — switching brands just swaps the swatch grid.
// ─────────────────────────────────────────────────────────────────────────
type PaintColor = { name: string; hex: string };
type Brand = { id: string; name: string; tagline: string; colors: PaintColor[] };

const BRANDS: Brand[] = [
  {
    id: "behr",
    name: "BEHR PREMIUM",
    tagline: "Top-rated interior paint",
    colors: [
      { name: "Ballet White",     hex: "#F1ECE0" },
      { name: "Silver Drop",      hex: "#C9CDD0" },
      { name: "Dolphin Gray",     hex: "#7C8589" },
      { name: "Mountain Shadow",  hex: "#3D4148" },
      { name: "Midnight Navy",    hex: "#1F2A44" },
      { name: "Forest Floor",     hex: "#2E3A2A" },
      { name: "Baked Clay",       hex: "#B96A4E" },
      { name: "Crabapple",        hex: "#9C2B2E" },
    ],
  },
  {
    id: "glidden",
    name: "GLIDDEN PREMIUM",
    tagline: "Paint + Primer in one",
    colors: [
      { name: "Whisper White",    hex: "#F4F1EB" },
      { name: "Sandy Beige",      hex: "#D6C5A8" },
      { name: "Stone Gray",       hex: "#9A9A95" },
      { name: "Charcoal Smoke",   hex: "#4A4A48" },
      { name: "Deep Sea Blue",    hex: "#234B6E" },
      { name: "Sage Whisper",     hex: "#9DAE96" },
      { name: "Terracotta Pot",   hex: "#B5674D" },
      { name: "Burgundy Velvet",  hex: "#6E2C36" },
    ],
  },
  {
    id: "ppg",
    name: "PPG TIMELESS",
    tagline: "Walls that last",
    colors: [
      { name: "Alabaster",        hex: "#EDE6D6" },
      { name: "Pale Oak",         hex: "#D6CCB6" },
      { name: "Putty Gray",       hex: "#A39E92" },
      { name: "Iron Mountain",    hex: "#3F4042" },
      { name: "Indigo Batik",     hex: "#2C3E5C" },
      { name: "Olive Grove",      hex: "#6B7548" },
      { name: "Persimmon",        hex: "#D26A4A" },
      { name: "Plum Wine",        hex: "#5C2C44" },
    ],
  },
  {
    id: "kilz",
    name: "KILZ COMPLETE",
    tagline: "Coverage + color",
    colors: [
      { name: "Cotton Tail",      hex: "#F5F2E8" },
      { name: "Driftwood",        hex: "#C7B49A" },
      { name: "River Rock",       hex: "#8A8579" },
      { name: "Black Sable",      hex: "#2D2D2A" },
      { name: "Harbor Blue",      hex: "#2E4A6B" },
      { name: "Cactus Garden",    hex: "#5E6E48" },
      { name: "Adobe Sunrise",    hex: "#C77952" },
      { name: "Vintage Rose",     hex: "#A24A56" },
    ],
  },
];

const SHEENS = [
  { id: "matte",      label: "Matte",      brightness: 0.95, contrast: 0.95, saturate: 0.85 },
  { id: "eggshell",   label: "Eggshell",   brightness: 1.00, contrast: 1.00, saturate: 0.95 },
  { id: "satin",      label: "Satin",      brightness: 1.05, contrast: 1.05, saturate: 1.00 },
  { id: "semigloss",  label: "Semi-Gloss", brightness: 1.10, contrast: 1.15, saturate: 1.05 },
  { id: "gloss",      label: "Gloss",      brightness: 1.18, contrast: 1.25, saturate: 1.10 },
];

const COATS = [
  { id: 1, label: "1 Coat", saturate: 0.55, brightness: 1.05 },
  { id: 2, label: "2 Coats", saturate: 0.85, brightness: 1.00 },
  { id: 3, label: "3 Coats", saturate: 1.10, brightness: 0.97 },
];

// ─────────────────────────────────────────────────────────────────────────
// Default state. The user starts on the bedroom with BEHR Dolphin Gray
// at satin, 2 coats.
// ─────────────────────────────────────────────────────────────────────────
const DEFAULT_BRAND_INDEX = 0;
const DEFAULT_COLOR_INDEX = 2;          // Dolphin Gray
const DEFAULT_ROOM_INDEX = 2;           // kitchen-bright (lighter wall shows tint effect better)
const DEFAULT_SHEEN_INDEX = 2;          // satin
const DEFAULT_COATS_INDEX = 1;          // 2 coats
const DEFAULT_HEX = BRANDS[DEFAULT_BRAND_INDEX].colors[DEFAULT_COLOR_INDEX].hex;

// ─────────────────────────────────────────────────────────────────────────
// Color → hue (in radians) conversion.
//
// The engine's filter pipeline (src/gl/rectPipeline.ts:322-326) shifts
// the source image's hue by a fixed amount in HSL space. So given a
// *target* paint color and the source image's current average hue, we
// compute the rotation that would land the average on the target.
//
// For simplicity we use a heuristic: compute the target color's hue,
// subtract the assumed "neutral white wall" hue of 0, and apply that
// rotation. The brightness / contrast / saturation filters handle the
// rest of the realism (sheen, coverage).
// ─────────────────────────────────────────────────────────────────────────
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([a-fA-F0-9]{2})([a-fA-F0-9]{2})([a-fA-F0-9]{2})$/.exec(hex);
  if (!m) return { r: 1, g: 1, b: 1 };
  return {
    r: parseInt(m[1], 16) / 255,
    g: parseInt(m[2], 16) / 255,
    b: parseInt(m[3], 16) / 255,
  };
}

/** RGB → HSL hue in radians (0 .. 2π). Used by applyState to compute
 *  the hue rotation that shifts the photo's pixels to the paint color.
 *  0 = red, 2π/3 ≈ 2.094 = green, 4π/3 ≈ 4.189 = blue, π ≈ 3.14 = cyan,
 *  π/2 ≈ 1.57 = yellow-green, etc. */
function rgbToHueRad(r: number, g: number, b: number): number {
  const maxC = Math.max(r, g, b);
  const minC = Math.min(r, g, b);
  const d = maxC - minC;
  if (d === 0) return 0;
  let h: number;
  if (maxC === r) h = ((g - b) / d) % 6;
  else if (maxC === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = h / 6;
  if (h < 0) h += 1;
  return h * Math.PI * 2;
}

// ─────────────────────────────────────────────────────────────────────────
// Responsive layout — same helper shape as the chase demo.
// ─────────────────────────────────────────────────────────────────────────
const STACKED_BREAKPOINT = 720;

function pickPaintLayout(w: number, h: number) {
  const isStacked = w < STACKED_BREAKPOINT;
  if (isStacked) {
    // On mobile: photo (right) on TOP, form (left) on BOTTOM. The user
    // wants to see the painted room first, then adjust colors below.
    // Photo panel gets 55% of the height so a portrait room photo fits
    // without being cut off.
    const photoH = Math.floor(h * 0.55);
    const formH = h - photoH;
    return {
      mode: "stacked" as const,
      leftPanel:  { x: 0, y: photoH,    w, h: formH },
      rightPanel: { x: 0, y: 0,         w, h: photoH },
      pad: 12,
    };
  }
  const halfW = Math.floor(w / 2);
  return {
    mode: "horizontal" as const,
    leftPanel:  { x: 0,      y: 0, w: halfW, h },
    rightPanel: { x: halfW,  y: 0, w: w - halfW, h },
    pad: 22,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// SceneNode refs exposed to the DOM overlay for live mutation.
// ─────────────────────────────────────────────────────────────────────────
export type PaintRefs = {
  scene: Scene;
  /** The room photo — imageUrl changes when user picks a different room. */
  heroImage: SceneNode;
  /** The tint overlay rect — applies the color/sheen filter chain to
   *  everything underneath. Mutating its filter.{hueRotate,saturate,
   *  brightness,contrast} is what drives the live repaint. */
  tintOverlay: SceneNode;
  /** Caption text nodes — read-only display of current brand/color/sheen. */
  brandText: SceneNode;
  colorText: SceneNode;
  hexText: SceneNode;
  sheenText: SceneNode;
};

// ─────────────────────────────────────────────────────────────────────────
// Scene factory.
// ─────────────────────────────────────────────────────────────────────────
export function scenePaint(w: number, h: number): Scene {
  const scene = createScene();
  const layout = pickPaintLayout(w, h);
  const right = layout.rightPanel;
  const panelLeft = right.x;
  const panelTop = right.y;
  const panelRight = right.w;
  const panelBottom = right.h;

  let z = 0;
  const add = (n: SceneNode) => {
    n.zIndex = z++;
    addNode(scene, n);
  };

  // Background — covers everything; tinted by the overlay above.
  add(makeRect({
    x: 0, y: 0, w, h,
    fill: { r: 0.04, g: 0.04, b: 0.05, a: 1 },
  }));

  // Divider line on desktop; horizontal divider on mobile.
  if (layout.mode === "horizontal") {
    add(makeRect({
      x: panelLeft - 1, y: 0, w: 1, h,
      fill: { r: 1, g: 1, b: 1, a: 0.06 },
    }));
  } else {
    add(makeRect({
      x: 0, y: panelTop - 1, w, h: 1,
      fill: { r: 1, g: 1, b: 1, a: 0.06 },
    }));
  }

  // Black backplate for the photo panel.
  add(makeRect({
    x: panelLeft, y: panelTop, w: panelRight, h: panelBottom,
    fill: { r: 0, g: 0, b: 0, a: 1 },
  }));

  // The room photo — full-bleed image on the right/bottom.
  const heroImage = makeImage({
    x: panelLeft, y: panelTop, w: panelRight, h: panelBottom,
    imageUrl: ROOMS[DEFAULT_ROOM_INDEX].url,
    radius: 0,
    tagName: "paint-hero-image",
  });
  add(heroImage);

  // ── Tint overlay ─────────────────────────────────────────────────────
  // The NSPP primitive for this demo: a full-bleed rect drawn with the
  // paint color as its fill and mix-blend-mode="multiply" — but kept at
  // ZERO alpha so it doesn't visibly darken. The actual repaint is
  // driven by hueRotate + saturate on the heroImage filter chain
  // (see applyState). The overlay exists only because the original
  // design used the blend mode; we keep it as a no-op safety so any
  // future filter chain additions still compose predictably.
  const tintOverlay = makeRect({
    x: panelLeft, y: panelTop, w: panelRight, h: panelBottom,
    fill: { r: 1, g: 1, b: 1, a: 0 },       // invisible — driver is heroImage.filter
    mixBlendMode: "multiply",
    tagName: "paint-tint-overlay",
  });
  add(tintOverlay);

  // ── Caption block (bottom-left of photo panel) ───────────────────────
  const captionPad = layout.pad;
  const captionName = makeText({
    x: panelLeft + captionPad,
    y: panelTop + panelBottom - captionPad - 110,
    w: panelRight - captionPad * 2,
    h: 36,
    text: BRANDS[DEFAULT_BRAND_INDEX].name,
    fontSize: 22,
    fontWeight: 800,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    textWrap: false,
    letterSpacing: 0.4,
    tagName: "paint-brand-text",
  });
  add(captionName);

  const captionColor = makeText({
    x: panelLeft + captionPad,
    y: panelTop + panelBottom - captionPad - 70,
    w: panelRight - captionPad * 2,
    h: 30,
    text: BRANDS[DEFAULT_BRAND_INDEX].colors[DEFAULT_COLOR_INDEX].name,
    fontSize: 28,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    textWrap: false,
    tagName: "paint-color-text",
  });
  add(captionColor);

  const captionHex = makeText({
    x: panelLeft + captionPad,
    y: panelTop + panelBottom - captionPad - 38,
    w: panelRight - captionPad * 2,
    h: 22,
    text: DEFAULT_HEX.toUpperCase() + "  ·  " + SHEENS[DEFAULT_SHEEN_INDEX].label + "  ·  " + COATS[DEFAULT_COATS_INDEX].label,
    fontSize: 13,
    fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 0.85 },
    textWrap: false,
    letterSpacing: 0.6,
    tagName: "paint-hex-text",
  });
  add(captionHex);

  const captionSheen = makeText({
    x: panelLeft + captionPad,
    y: panelTop + captionPad + 12,
    w: panelRight - captionPad * 2,
    h: 16,
    text: "ROOM: " + ROOMS[DEFAULT_ROOM_INDEX].label,
    fontSize: 11,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 0.65 },
    textWrap: false,
    letterSpacing: 1.2,
    tagName: "paint-sheen-text",
  });
  add(captionSheen);

  // "PAINT VISUALIZER · LIVE" badge top-left of photo panel.
  add(makeRect({
    x: panelLeft + 14, y: panelTop + 14, w: 156, h: 28,
    radius: 14,
    fill: { r: 0, g: 0, b: 0, a: 0.55 },
  }));
  add(makeRect({
    x: panelLeft + 22, y: panelTop + 22, w: 8, h: 8,
    radius: 4,
    fill: { r: 0.94, g: 0.27, b: 0.27, a: 1 },
  }));
  add(makeText({
    x: panelLeft + 36, y: panelTop + 20, w: 130, h: 16,
    text: "PAINT · VISUALIZER",
    fontSize: 11,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    letterSpacing: 1.4,
  }));

  return scene;
}

// ─────────────────────────────────────────────────────────────────────────
// DOM overlay (left panel). Real HTML inputs that mutate the live
// SceneNode refs. Same pattern as chaseDemo.ts — this is the one we
// keep rebuilding because it's the right shape.
// ─────────────────────────────────────────────────────────────────────────
const OVERLAY_ID = "paint-overlay";

// Current state — also held in window.__paintState so any external
// trigger (URL deep-link, voice assistant, etc.) can drive the demo.
type PaintState = {
  brandIndex: number;
  colorIndex: number;
  sheenIndex: number;
  coatsIndex: number;
  roomIndex: number;
  customHex: string | null;   // if user types a hex, override the swatch
};

const state: PaintState = {
  brandIndex: DEFAULT_BRAND_INDEX,
  colorIndex: DEFAULT_COLOR_INDEX,
  sheenIndex: DEFAULT_SHEEN_INDEX,
  coatsIndex: DEFAULT_COATS_INDEX,
  roomIndex: DEFAULT_ROOM_INDEX,
  customHex: null,
};

/** The current hex the user has chosen (custom if typed, otherwise
 *  the active brand palette swatch). */
function currentHex(): string {
  if (state.customHex) return state.customHex;
  return BRANDS[state.brandIndex].colors[state.colorIndex].hex;
}

/** Apply the current state to the live scene refs. */
function applyState(refs: PaintRefs): void {
  const hex = currentHex();
  const rgb = hexToRgb(hex);
  const sheen = SHEENS[state.sheenIndex];
  const coats = COATS[state.coatsIndex];

  // 1. The tint overlay rect is invisible (alpha 0); the actual repaint
  //    is driven entirely by the heroImage's per-instance filter chain.
  //    The overlay rect stays in the scene at tagName "paint-tint-overlay"
  //    because it's referenced by the buildPaintRefs walker and used in
  //    future variations (e.g. sheen's gloss highlighting).
  refs.tintOverlay.fill = {
    r: rgb.r, g: rgb.g, b: rgb.b, a: 0,
  };

  // 2. The REAL paint visualizer primitive: shift every pixel's hue in
  //    the photo to the paint color's hue. This is exactly what CSS
  //    `filter: hue-rotate(...)` does, and the engine implements it as
  //    an HSL round-trip in the shader (src/gl/rectPipeline.ts:218-326).
  //    The wall genuinely repaints: white wall → blue wall, etc.
  //
  //    Hue rotation is in RADIANS (0 to 2π for full rotation). The
  //    paint color's hue is its position on the color wheel (0=red,
  //    2π/3=green, 4π/3=blue, π=cyan, etc.). To repaint the room
  //    toward the target hue, we shift the room's pixels to that
  //    absolute hue position.
  const paintHue = rgbToHueRad(rgb.r, rgb.g, rgb.b);
  // Sheen + coats modulate saturation (coats = how much color shows;
  // sheen = matte reduces saturation, gloss increases it).
  // Sheen boosts brightness (gloss = brighter reflection).
  refs.heroImage.filter = {
    blur: 0,
    brightness: sheen.brightness * coats.brightness,
    contrast: sheen.contrast,
    // Saturate: 1.4 at full coverage makes the colors vivid. Matte drops
    // to ~0.85 (looks chalky). 3 coats saturates fully.
    saturate: sheen.saturate * coats.saturate * 1.4,
    hueRotate: paintHue,
    invert: 0,
  };

  // 4. Caption text reflects current state.
  const brand = BRANDS[state.brandIndex];
  const color = brand.colors[state.colorIndex];
  refs.brandText.text = brand.name;
  refs.colorText.text = color.name;
  refs.hexText.text =
    hex.toUpperCase() +
    "  ·  " + sheen.label +
    "  ·  " + coats.label;
  refs.sheenText.text = "ROOM: " + ROOMS[state.roomIndex].label;

  // 5. Hero image URL — change when room picker changes.
  refs.heroImage.imageUrl = ROOMS[state.roomIndex].url;
}

export function mountPaintOverlay(w: number, h: number): void {
  if (document.getElementById(OVERLAY_ID)) return;

  const layout = pickPaintLayout(w, h);
  const left = layout.leftPanel;
  const pad = layout.pad;
  const isMobile = layout.mode === "stacked";

  const root = document.createElement("div");
  root.id = OVERLAY_ID;
  root.style.cssText = `
    position: fixed;
    left: ${left.x}px; top: ${left.y}px;
    width: ${left.w}px;
    height: ${left.h}px;
    background: linear-gradient(180deg, #0a0a0c 0%, #14141a 100%);
    display: flex;
    flex-direction: column;
    padding: ${pad}px;
    gap: ${isMobile ? "6px" : Math.round(pad * 0.5)}px;
    color: #fafafa;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    z-index: 5;
    box-sizing: border-box;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
  `;

  // ── Header ──────────────────────────────────────────────────────────
  // On mobile, drop the subtitle ("Pick a brand, color, sheen...") so the
  // form panel fits without scrolling on a 428×926 viewport. Hero text
  // shrunk from 20px to 16px.
  const header = document.createElement("div");
  header.innerHTML = isMobile
    ? `
    <div style="font-size: 9px; letter-spacing: 0.14em; color: rgba(255,255,255,0.55); font-weight: 600;">
      HOME DEPOT · PAINT VISUALIZER
    </div>
    <div style="font-size: 15px; font-weight: 800; letter-spacing: -0.01em; margin-top: 2px;">
      Try a color on your wall
    </div>
  `
    : `
    <div style="font-size: 10px; letter-spacing: 0.16em; color: rgba(255,255,255,0.55); font-weight: 600;">
      HOME DEPOT · PAINT VISUALIZER
    </div>
    <div style="font-size: 20px; font-weight: 800; letter-spacing: -0.01em; margin-top: 4px;">
      Try a color on your wall
    </div>
    <div style="font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 4px;">
      Pick a brand, color, sheen. The right panel updates live.
    </div>
  `;
  root.appendChild(header);

  // ── Room thumbnails (6 small images) ────────────────────────────────
  const roomLab = document.createElement("div");
  roomLab.style.cssText = isMobile
    ? "font-size: 9px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 4px;"
    : "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 8px;";
  roomLab.textContent = "ROOM";
  root.appendChild(roomLab);
  const roomRow = document.createElement("div");
  roomRow.style.cssText = "display: flex; gap: 6px; flex-wrap: wrap;";
  const roomRefs: HTMLDivElement[] = [];
  ROOMS.forEach((room, i) => {
    const t = document.createElement("div");
    t.dataset.cfRoom = String(i);
    // Smaller thumbs on mobile: 44×32 (was 50×40). Even more compact.
    const thumbW = isMobile ? 44 : 64;
    const thumbH = isMobile ? 32 : 50;
    t.style.cssText = `
      width: ${thumbW}px; height: ${thumbH}px;
      border-radius: 6px;
      background-image: url('${room.url}');
      background-size: cover;
      background-position: center;
      border: 2px solid ${i === state.roomIndex ? "rgba(251,146,60,0.95)" : "rgba(255,255,255,0.08)"};
      box-shadow: ${i === state.roomIndex ? "0 0 0 2px rgba(251,146,60,0.25)" : "none"};
      cursor: pointer;
      flex-shrink: 0;
      transition: transform 0.15s, border-color 0.15s;
    `;
    t.addEventListener("mouseenter", () => { t.style.transform = "translateY(-2px)"; });
    t.addEventListener("mouseleave", () => { t.style.transform = "translateY(0)"; });
    roomRow.appendChild(t);
    roomRefs.push(t);
  });
  root.appendChild(roomRow);

  // ── Brand picker (4 brand chips) ────────────────────────────────────
  const brandLab = document.createElement("div");
  brandLab.style.cssText = isMobile
    ? "font-size: 9px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 4px;"
    : "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 8px;";
  brandLab.textContent = "BRAND";
  root.appendChild(brandLab);
  const brandRow = document.createElement("div");
  brandRow.style.cssText = "display: flex; gap: 6px; flex-wrap: wrap;";
  const brandRefs: HTMLDivElement[] = [];
  BRANDS.forEach((brand, i) => {
    const c = document.createElement("div");
    c.dataset.cfBrand = String(i);
    c.style.cssText = isMobile
      ? `
      padding: 4px 8px;
      border-radius: 6px;
      background: ${i === state.brandIndex ? "rgba(251,146,60,0.18)" : "rgba(255,255,255,0.06)"};
      border: 1px solid ${i === state.brandIndex ? "rgba(251,146,60,0.55)" : "rgba(255,255,255,0.10)"};
      color: #fafafa;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.06em;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s, border-color 0.15s;
    `
      : `
      padding: 6px 10px;
      border-radius: 8px;
      background: ${i === state.brandIndex ? "rgba(251,146,60,0.18)" : "rgba(255,255,255,0.06)"};
      border: 1px solid ${i === state.brandIndex ? "rgba(251,146,60,0.55)" : "rgba(255,255,255,0.10)"};
      color: #fafafa;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s, border-color 0.15s;
    `;
    c.textContent = brand.id.toUpperCase();
    brandRow.appendChild(c);
    brandRefs.push(c);
  });
  root.appendChild(brandRow);

  // ── Color palette (8 swatches for the active brand) ────────────────
  const colorLab = document.createElement("div");
  colorLab.style.cssText = isMobile
    ? "font-size: 9px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 4px;"
    : "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 8px;";
  colorLab.textContent = "COLOR";
  root.appendChild(colorLab);
  const colorGrid = document.createElement("div");
  colorGrid.style.cssText = isMobile
    ? "display: grid; grid-template-columns: repeat(8, 1fr); gap: 4px;"
    : "display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;";
  const colorRefs: HTMLDivElement[] = [];
  // Re-built whenever the brand changes.
  function rebuildColorGrid(): void {
    colorGrid.innerHTML = "";
    colorRefs.length = 0;
    const brand = BRANDS[state.brandIndex];
    brand.colors.forEach((color, i) => {
      const s = document.createElement("div");
      s.dataset.cfColor = String(i);
      s.style.cssText = `
        width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 8px;
        background: ${color.hex};
        border: 2px solid ${(!state.customHex && i === state.colorIndex)
          ? "rgba(251,146,60,0.95)"
          : "rgba(255,255,255,0.12)"};
        box-shadow: ${(!state.customHex && i === state.colorIndex)
          ? "0 0 0 2px rgba(251,146,60,0.25)"
          : "none"};
        cursor: pointer;
        position: relative;
        transition: transform 0.15s, border-color 0.15s;
      `;
      // Tooltip on hover
      s.title = color.name + " · " + color.hex;
      s.addEventListener("mouseenter", () => {
        s.style.transform = "scale(1.05)";
      });
      s.addEventListener("mouseleave", () => {
        s.style.transform = "scale(1)";
      });
      s.addEventListener("click", () => {
        state.customHex = null;
        state.colorIndex = i;
        rebuildColorGrid();
        render();
      });
      colorGrid.appendChild(s);
      colorRefs.push(s);
    });
  }
  rebuildColorGrid();
  root.appendChild(colorGrid);

  // ── Custom hex input ────────────────────────────────────────────────
  const hexRow = document.createElement("div");
  hexRow.style.cssText = "display: flex; gap: 6px; align-items: center;";
  const hexLabel = document.createElement("div");
  hexLabel.style.cssText = isMobile
    ? "font-size: 9px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); flex: 0 0 auto; margin-right: 6px;"
    : "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); flex: 0 0 auto;";
  hexLabel.textContent = "CUSTOM";
  const hexInput = document.createElement("input");
  hexInput.type = "text";
  hexInput.placeholder = "#FF8800";
  hexInput.value = "";
  hexInput.style.cssText = isMobile
    ? `
    flex: 1 1 auto;
    min-width: 0;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 6px;
    padding: 5px 8px;
    font-size: 11px; font-weight: 600;
    color: #fafafa;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
  `
    : `
    flex: 1 1 auto;
    min-width: 0;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px; font-weight: 600;
    color: #fafafa;
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
  `;
  hexInput.addEventListener("focus", () => {
    hexInput.style.borderColor = "rgba(251,146,60,0.6)";
    hexInput.style.background = "rgba(255,255,255,0.08)";
  });
  hexInput.addEventListener("blur", () => {
    hexInput.style.borderColor = "rgba(255,255,255,0.10)";
    hexInput.style.background = "rgba(255,255,255,0.06)";
  });
  hexInput.addEventListener("input", () => {
    const v = hexInput.value.trim();
    if (/^#?[a-fA-F0-9]{6}$/.test(v)) {
      state.customHex = v.startsWith("#") ? v : "#" + v;
      rebuildColorGrid();
      render();
    } else if (v === "") {
      state.customHex = null;
      rebuildColorGrid();
      render();
    }
  });
  hexRow.appendChild(hexLabel);
  hexRow.appendChild(hexInput);
  root.appendChild(hexRow);

  // ── Sheen slider (5 stops) ──────────────────────────────────────────
  const sheenLab = document.createElement("div");
  sheenLab.style.cssText = isMobile
    ? "font-size: 9px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 4px; display: flex; justify-content: space-between;"
    : "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 8px; display: flex; justify-content: space-between;";
  sheenLab.innerHTML = `<span>SHEEN</span><span style="font-weight: 700; color: rgba(251,146,60,0.95);" id="paint-sheen-current">${SHEENS[state.sheenIndex].label}</span>`;
  root.appendChild(sheenLab);
  const sheenRow = document.createElement("div");
  sheenRow.style.cssText = "display: flex; gap: 4px;";
  SHEENS.forEach((sheen, i) => {
    const b = document.createElement("div");
    b.style.cssText = isMobile
      ? `
      flex: 1 1 0;
      text-align: center;
      padding: 4px 1px;
      border-radius: 5px;
      background: ${i === state.sheenIndex ? "rgba(251,146,60,0.18)" : "rgba(255,255,255,0.04)"};
      border: 1px solid ${i === state.sheenIndex ? "rgba(251,146,60,0.55)" : "rgba(255,255,255,0.08)"};
      color: ${i === state.sheenIndex ? "#fed7aa" : "rgba(255,255,255,0.65)"};
      font-size: 8px; font-weight: 700;
      letter-spacing: 0.02em;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s, color 0.15s;
    `
      : `
      flex: 1 1 0;
      text-align: center;
      padding: 6px 2px;
      border-radius: 6px;
      background: ${i === state.sheenIndex ? "rgba(251,146,60,0.18)" : "rgba(255,255,255,0.04)"};
      border: 1px solid ${i === state.sheenIndex ? "rgba(251,146,60,0.55)" : "rgba(255,255,255,0.08)"};
      color: ${i === state.sheenIndex ? "#fed7aa" : "rgba(255,255,255,0.65)"};
      font-size: 9px; font-weight: 700;
      letter-spacing: 0.04em;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s, color 0.15s;
    `;
    b.textContent = sheen.label;
    b.addEventListener("click", () => {
      state.sheenIndex = i;
      // Update sheen buttons in place.
      sheenRow.querySelectorAll("div").forEach((el, j) => {
        const active = j === i;
        (el as HTMLElement).style.background = active
          ? "rgba(251,146,60,0.18)"
          : "rgba(255,255,255,0.04)";
        (el as HTMLElement).style.borderColor = active
          ? "rgba(251,146,60,0.55)"
          : "rgba(255,255,255,0.08)";
        (el as HTMLElement).style.color = active
          ? "#fed7aa"
          : "rgba(255,255,255,0.65)";
      });
      const sheenNow = document.getElementById("paint-sheen-current");
      if (sheenNow) sheenNow.textContent = sheen.label;
      render();
    });
    sheenRow.appendChild(b);
  });
  root.appendChild(sheenRow);

  // ── Coats slider (3 stops) ──────────────────────────────────────────
  const coatsLab = document.createElement("div");
  coatsLab.style.cssText = isMobile
    ? "font-size: 9px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 4px; display: flex; justify-content: space-between;"
    : "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55); margin-top: 8px; display: flex; justify-content: space-between;";
  coatsLab.innerHTML = `<span>COVERAGE</span><span style="font-weight: 700; color: rgba(251,146,60,0.95);" id="paint-coats-current">${COATS[state.coatsIndex].label}</span>`;
  root.appendChild(coatsLab);
  const coatsRow = document.createElement("div");
  coatsRow.style.cssText = "display: flex; gap: 4px;";
  COATS.forEach((coats, i) => {
    const b = document.createElement("div");
    b.style.cssText = isMobile
      ? `
      flex: 1 1 0;
      text-align: center;
      padding: 4px 2px;
      border-radius: 5px;
      background: ${i === state.coatsIndex ? "rgba(251,146,60,0.18)" : "rgba(255,255,255,0.04)"};
      border: 1px solid ${i === state.coatsIndex ? "rgba(251,146,60,0.55)" : "rgba(255,255,255,0.08)"};
      color: ${i === state.coatsIndex ? "#fed7aa" : "rgba(255,255,255,0.65)"};
      font-size: 9px; font-weight: 700;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s, color 0.15s;
    `
      : `
      flex: 1 1 0;
      text-align: center;
      padding: 6px 2px;
      border-radius: 6px;
      background: ${i === state.coatsIndex ? "rgba(251,146,60,0.18)" : "rgba(255,255,255,0.04)"};
      border: 1px solid ${i === state.coatsIndex ? "rgba(251,146,60,0.55)" : "rgba(255,255,255,0.08)"};
      color: ${i === state.coatsIndex ? "#fed7aa" : "rgba(255,255,255,0.65)"};
      font-size: 10px; font-weight: 700;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s, color 0.15s;
    `;
    b.textContent = coats.label;
    b.addEventListener("click", () => {
      state.coatsIndex = i;
      coatsRow.querySelectorAll("div").forEach((el, j) => {
        const active = j === i;
        (el as HTMLElement).style.background = active
          ? "rgba(251,146,60,0.18)"
          : "rgba(255,255,255,0.04)";
        (el as HTMLElement).style.borderColor = active
          ? "rgba(251,146,60,0.55)"
          : "rgba(255,255,255,0.08)";
        (el as HTMLElement).style.color = active
          ? "#fed7aa"
          : "rgba(255,255,255,0.65)";
      });
      const coatsNow = document.getElementById("paint-coats-current");
      if (coatsNow) coatsNow.textContent = coats.label;
      render();
    });
    coatsRow.appendChild(b);
  });
  root.appendChild(coatsRow);

  // ── The trigger: render() applies current state to live refs. ─────
  // We close over refs from the publish step in main.ts.
  function render(): void {
    const refs = window.__paintRefs;
    if (!refs) return;
    applyState(refs);
  }

  // Room thumbnail click handlers.
  roomRefs.forEach((t, i) => {
    t.addEventListener("click", () => {
      state.roomIndex = i;
      roomRefs.forEach((el, j) => {
        const active = j === i;
        el.style.border = active
          ? "2px solid rgba(251,146,60,0.95)"
          : "2px solid rgba(255,255,255,0.08)";
        el.style.boxShadow = active
          ? "0 0 0 2px rgba(251,146,60,0.25)"
          : "none";
      });
      render();
    });
  });

  // Brand chip click handlers.
  brandRefs.forEach((b, i) => {
    b.addEventListener("click", () => {
      state.brandIndex = i;
      state.colorIndex = 0;
      state.customHex = null;
      hexInput.value = "";
      brandRefs.forEach((el, j) => {
        const active = j === i;
        el.style.background = active
          ? "rgba(251,146,60,0.18)"
          : "rgba(255,255,255,0.06)";
        el.style.borderColor = active
          ? "rgba(251,146,60,0.55)"
          : "rgba(255,255,255,0.10)";
      });
      rebuildColorGrid();
      render();
    });
  });

  // Mount.
  document.body.appendChild(root);

  // First render to apply the default state.
  render();
}

export function unmountPaintOverlay(): void {
  const root = document.getElementById(OVERLAY_ID);
  if (root && root.parentElement) root.parentElement.removeChild(root);
  window.__paintRefs = undefined;
}

declare global {
  interface Window {
    __paintRefs?: PaintRefs;
  }
}

export function publishPaintRefs(refs: PaintRefs): PaintRefs {
  window.__paintRefs = refs;
  return refs;
}

// ─────────────────────────────────────────────────────────────────────────
// One-stop export.
// ─────────────────────────────────────────────────────────────────────────
export const paintDemoRoute = {
  scene: scenePaint,
  mount: mountPaintOverlay,
  unmount: unmountPaintOverlay,
  publish: publishPaintRefs,
};